import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { useServerEnv } from '@hh.ru/magritte-common-is-server-env';
import { disableScroll } from '@hh.ru/magritte-common-modal-helper';
import { useCloseByClickOutside } from '@hh.ru/magritte-common-mouse';
import { useSwipe } from '@hh.ru/magritte-common-use-swipe';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Layer } from '@hh.ru/magritte-ui-layer';

var styles = {"animation-timeout":"magritte-animation-timeout___cwIwT_2-0-4","animationTimeout":"magritte-animation-timeout___cwIwT_2-0-4","enter-animation-active":"magritte-enter-animation-active___Qti02_2-0-4","enterAnimationActive":"magritte-enter-animation-active___Qti02_2-0-4","exit-animation-active":"magritte-exit-animation-active___deHqY_2-0-4","exitAnimationActive":"magritte-exit-animation-active___deHqY_2-0-4","opacity-animation":"magritte-opacity-animation___m7RYi_2-0-4","opacityAnimation":"magritte-opacity-animation___m7RYi_2-0-4","enter-animation":"magritte-enter-animation___CbUHN_2-0-4","enterAnimation":"magritte-enter-animation___CbUHN_2-0-4","exit-animation":"magritte-exit-animation___d2AJ4_2-0-4","exitAnimation":"magritte-exit-animation___d2AJ4_2-0-4","overlay":"magritte-overlay___hMlKJ_2-0-4","mobile-container":"magritte-mobile-container___dQRCl_2-0-4","mobileContainer":"magritte-mobile-container___dQRCl_2-0-4","desktop-container":"magritte-desktop-container___26vkq_2-0-4","desktopContainer":"magritte-desktop-container___26vkq_2-0-4","main":"magritte-main___iCU-E_2-0-4","icon":"magritte-icon___fLpa-_2-0-4","icon_neutral":"magritte-icon_neutral___Jyl3l_2-0-4","iconNeutral":"magritte-icon_neutral___Jyl3l_2-0-4","icon_accent":"magritte-icon_accent___Nn-T1_2-0-4","iconAccent":"magritte-icon_accent___Nn-T1_2-0-4","icon_positive":"magritte-icon_positive___OU9Zu_2-0-4","iconPositive":"magritte-icon_positive___OU9Zu_2-0-4","icon_warning":"magritte-icon_warning___7BbUi_2-0-4","iconWarning":"magritte-icon_warning___7BbUi_2-0-4","icon_negative":"magritte-icon_negative___s0OA8_2-0-4","iconNegative":"magritte-icon_negative___s0OA8_2-0-4","title":"magritte-title___yG219_2-0-4","description":"magritte-description___KeNoT_2-0-4","buttons":"magritte-buttons___lyxBC_2-0-4","buttons_vertical":"magritte-buttons_vertical___8lPCo_2-0-4","buttonsVertical":"magritte-buttons_vertical___8lPCo_2-0-4"};

const OPACITY_ANIMATION_CLASSES = {
    enter: classnames(styles.opacityAnimation, styles.enterAnimation),
    enterActive: classnames(styles.enterAnimationActive, styles.opacityAnimation),
    exit: classnames(styles.opacityAnimation, styles.exitAnimation),
    exitActive: classnames(styles.exitAnimationActive, styles.opacityAnimation),
};
const TRANSFORM_ANIMATION_CLASSES = {
    enterActive: styles.enterAnimationActive,
    exitActive: styles.exitAnimationActive,
};
const VIEWPORT_HEIGHT = (typeof window !== 'undefined' ? window.visualViewport?.height : null) ?? 0;
const SWIPE_THRESHOLD_REF = { current: { max: Math.round(VIEWPORT_HEIGHT * 0.8) } };
const toInteger = (value) => {
    const result = parseInt(value, 10);
    return !Number.isNaN(result) ? result : 0;
};
const withStopPropagation = (handler) => {
    return (event) => {
        event.stopPropagation();
        handler?.(event);
    };
};
const OpacityAnimation = ({ animationTimeout, visible, render }) => {
    const animatedElementRef = useRef(null);
    return (jsx(CSSTransition, { appear: true, classNames: OPACITY_ANIMATION_CLASSES, in: visible, mountOnEnter: true, nodeRef: animatedElementRef, timeout: animationTimeout, unmountOnExit: true, children: render(animatedElementRef) }));
};
const TransformAnimation = ({ animationTimeout, visible, render, onClose, }) => {
    const animatedElementRef = useRef(null);
    const swipeOffsetRef = useRef(0);
    const runTranslateYAnimation = () => {
        if (animatedElementRef.current !== null && visualViewport !== null) {
            animatedElementRef.current.style.transform = `translateY(${visualViewport.height + swipeOffsetRef.current - animatedElementRef.current.getBoundingClientRect().top}px)`;
        }
    };
    const resetTranslateY = () => {
        if (animatedElementRef.current !== null) {
            animatedElementRef.current.style.transform = `translateY(${swipeOffsetRef.current}px)`;
        }
    };
    const resetSwipeOffset = () => {
        swipeOffsetRef.current = 0;
    };
    const swipeHandlers = useSwipe({
        thresholdYRef: SWIPE_THRESHOLD_REF,
        onSwipeMove: (event) => {
            if (animatedElementRef.current !== null) {
                animatedElementRef.current.style.transform = `translateY(${Math.max(event.distanceY, 0)}px)`;
            }
        },
        onSwipeEnd: (event) => {
            swipeOffsetRef.current = event.distanceY;
            onClose();
        },
        onSwipeCancel: () => {
            if (animatedElementRef.current !== null) {
                animatedElementRef.current.style.transform = `translateY(0px)`;
            }
        },
    });
    return (jsx(CSSTransition, { appear: true, classNames: TRANSFORM_ANIMATION_CLASSES, in: visible, mountOnEnter: true, nodeRef: animatedElementRef, onEnter: runTranslateYAnimation, onEntering: resetTranslateY, onExit: resetTranslateY, onExiting: runTranslateYAnimation, onExited: resetSwipeOffset, timeout: animationTimeout, unmountOnExit: true, children: render(animatedElementRef, {
            ...swipeHandlers,
            onTouchStart: withStopPropagation(swipeHandlers.onTouchStart),
            onTouchMove: withStopPropagation(swipeHandlers.onTouchMove),
        }) }));
};
const Alert = ({ iconStyle = 'neutral', layout, icon, title, description, buttons, visible, onClose, }) => {
    const [animationTimeout, setAnimationTimeout] = useState(null);
    const clickElementRef = useRef(null);
    const { isMobile } = useBreakpoint();
    const isServerEnv = useServerEnv();
    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.animationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const enter = toInteger(style.getPropertyValue('--enter-animation-duration'));
        const exit = toInteger(style.getPropertyValue('--exit-animation-duration'));
        setAnimationTimeout({ enter, exit });
        document.body.removeChild(animationTimeoutElement);
    }, [setAnimationTimeout]);
    useEffect(() => {
        if (visible) {
            return disableScroll();
        }
        return void 0;
    }, [visible]);
    useCloseByClickOutside(clickElementRef, onClose, visible);
    if (!animationTimeout || isServerEnv) {
        return null;
    }
    const content = (jsxs(Fragment, { children: [jsxs("div", { className: styles.main, "data-qa": "magritte-alert", children: [icon && (jsx("div", { className: classnames(styles.icon, { [styles[`icon_${iconStyle}`]]: iconStyle }), children: icon })), jsx("div", { className: styles.title, children: title }), description && jsx("div", { className: styles.description, children: description })] }), jsx("div", { className: classnames(styles.buttons, { [styles[`buttons_${layout}`]]: layout }), children: buttons })] }));
    if (isMobile) {
        return createPortal(jsxs(Fragment, { children: [jsx(OpacityAnimation, { animationTimeout: animationTimeout, visible: visible, render: (animatedElementRef) => (jsx(Layer, { layer: "bottom-sheet", children: jsx("div", { className: classnames(styles.overlay, styles.animationTimeout), onClick: onClose, ref: animatedElementRef }) })) }), jsx(TransformAnimation, { animationTimeout: animationTimeout, visible: visible, render: (animatedElementRef, props) => (jsx(Layer, { layer: "bottom-sheet", children: jsx("div", { ...props, className: classnames(styles.mobileContainer, styles.animationTimeout), ref: animatedElementRef, children: content }) })), onClose: onClose })] }), document.body);
    }
    return createPortal(jsx(OpacityAnimation, { animationTimeout: animationTimeout, visible: visible, render: (animatedElementRef) => (jsx(Layer, { layer: "modal", children: jsx("div", { className: classnames(styles.overlay, styles.animationTimeout), ref: animatedElementRef, children: jsx("div", { className: classnames(styles.desktopContainer), ref: clickElementRef, children: content }) }) })) }), document.body);
};

export { Alert, OpacityAnimation, TransformAnimation };
//# sourceMappingURL=Alert.js.map
